import { createSelector } from 'reselect';
import { PapiGallection } from '@wix/da-papi-types';
import {
  getDenormalizedDataEntityById,
  getDenormalizedDataEntitiesByIds,
} from '@wix/da-shared-react/pkg/redux/entities';
import { getUserById } from '@wix/da-shared-react/pkg/redux/users/selectors';
import { selectors as streamSelectors } from '@wix/da-shared-react/pkg/Stream';
import { GallectionType } from '@wix/da-shared-react/pkg/types/gallection';
import { flagEnabled } from '@wix/da-shared-react/pkg/redux/flags/selectors';
import { ALL_FOLDER_ID } from '@wix/da-url';
import { AppState } from '../../types/store';
import {
  getCurrentlyViewedFolderId,
  getGallectionSectionType,
} from './gallectionSection';
import { getDefaultFolderFromFolders } from '../components/Sections/GallectionsSection/helpers';
import {
  getStreamIdForGallectionType,
  getSchemaForGallectionType,
  filterOutEmpty,
} from '../utils/gallection';
import { getFolderDeviationsStreamId } from '../streams/folderDeviations';

export const getFolderById = (
  state: AppState,
  gallectionType: GallectionType,
  id: number
): PapiGallection | undefined =>
  getDenormalizedDataEntityById(
    state,
    getSchemaForGallectionType(gallectionType),
    id
  );

export const getFoldersByIds = (
  state: AppState,
  gallectionType: GallectionType,
  ids: number[]
) => {
  return getDenormalizedDataEntitiesByIds(
    state,
    getSchemaForGallectionType(gallectionType),
    ids
  );
};

function getFoldersForCurrentSection(state: AppState) {
  const folderType = getGallectionSectionType(state);
  return getFoldersByGallectionType(state, folderType);
}

export const getNonEmptyFolders = createSelector(
  [getFoldersForCurrentSection],
  folders => filterOutEmpty(folders)
);

export const getDefaultFolder = (state: AppState) =>
  getDefaultFolderFromFolders(getNonEmptyFolders(state));

function getFolderIdsByType(state: AppState, gallectionType: GallectionType) {
  return streamSelectors.getItems<number>(
    state,
    getStreamIdForGallectionType(gallectionType)
  );
}

function getFoldersByGallectionType(
  state: AppState,
  gallectionType: GallectionType
): PapiGallection[] {
  return getFoldersByIds(
    state,
    gallectionType,
    getFolderIdsByType(state, gallectionType)
  );
}

export const getFeaturedFolder = createSelector(
  [getFoldersForCurrentSection],
  folders => folders.find(({ name }) => name === 'Featured') || folders[0]
);

export const getScrapsFolder = createSelector(
  [getFoldersForCurrentSection],
  folders => folders.find(({ name }) => name === 'Scraps')
);

export const getAllDeviationsFolder = createSelector(
  [getNonEmptyFolders],
  folders => folders.find(({ folderId }) => folderId === ALL_FOLDER_ID)
);

export const hasAnyDeviations = (state: AppState): boolean => {
  const gallectionType = getGallectionSectionType(state);
  const currentlyViewedFolderId = getCurrentlyViewedFolderId(state);
  const streamId = getFolderDeviationsStreamId(
    gallectionType,
    currentlyViewedFolderId
  );
  const streamItems = streamSelectors.getItems(state, streamId) || [];

  const allFolder = getAllDeviationsFolder(state);
  const featuredFolder = getFeaturedFolder(state);
  const scrapsFolder = getScrapsFolder(state);
  return (
    (!!allFolder && allFolder.size > 0) ||
    (!!featuredFolder && featuredFolder.size > 0) ||
    (!!scrapsFolder && scrapsFolder.size > 0) ||
    !!streamItems.length
  );
};

/** Return the username to be used in the media selector to determine which
 *  gallections are used for selectable options. This is often either the
 *  profile owner user or a user able to edit widgets for a group. */
export const getMediaSelectorUsername = (
  state: AppState
): string | undefined => {
  // todo: remove and use profileOwnerUser.username when eclipsed_groups is removed
  const profileOwnerUser = getUserById(state, state.profileOwner.user.id);
  const eclipsed_groups = flagEnabled(state, 'eclipsed_groups');
  return eclipsed_groups ? profileOwnerUser.username : undefined;
};
